import { useEffect, useState, type KeyboardEvent } from "react";

import BirthYearFeedback from "./BirthYearFeedback";
import FieldHint from "./FieldHint";
import { ApiError, api, type TournamentDivision } from "../lib/api";
import { isKidsDivision } from "../lib/publicRegistrationMode";
import { GUARDIAN_AUTO_ENABLED_NOTICE, GUARDIAN_STILL_ON_HINT } from "../lib/registrationFields";
import {
  divisionBirthYearLabel,
  DIVISION_MODALITY_OPTIONS,
  applyDivisionModality,
  divisionDetailsLabel,
  divisionIsTeamPlay,
  divisionModality,
  type DivisionModality,
  divisionInputFromDraft,
  divisionsWithOwnDeadline,
  emptyDivisionDraft,
  shortDateEs,
  type TournamentDivisionDraft,
  weekdayDateEs,
  yearFromTournamentDate,
} from "../lib/tournamentDivisions";

type Props = {
  token: string;
  tournamentId: number;
  divisions: TournamentDivision[];
  onDivisionsChange: (divisions: TournamentDivision[]) => void;
  compact?: boolean;
  showFormatActions?: boolean;
  onCopyTournamentFormat?: (division: TournamentDivision) => void;
  tournamentStartDate?: string | null;
  /** Fecha final del torneo (AAAA-MM-DD): tope del cierre propio de una
   * categoría (puede caer después del inicio, Eduardo 2026-10-27). */
  tournamentEndDate?: string | null;
  /** Cierre general de inscripciones del torneo (AAAA-MM-DD): la fecha que
   * usan las categorías sin cierre propio. */
  tournamentRegistrationDeadline?: string | null;
  /** Avisa al Paso 1 si hay una categoría escrita sin agregar o una edición
   * sin guardar (null = nada pendiente), para no perderla al salir. */
  onPendingChange?: (pending: string | null) => void;
  /** El formulario trae la marca de que el sistema prendió los datos del
   * papá, mamá o tutor (primera categoría de menores, Eduardo 2026-10-03). */
  guardianFieldsAutoEnabled?: boolean;
};

function sortDivisions(divisions: TournamentDivision[]): TournamentDivision[] {
  return [...divisions].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
}

export default function TournamentDivisionManager({
  token,
  tournamentId,
  divisions,
  onDivisionsChange,
  compact = false,
  showFormatActions = false,
  onCopyTournamentFormat,
  tournamentStartDate,
  tournamentEndDate,
  tournamentRegistrationDeadline,
  onPendingChange,
  guardianFieldsAutoEnabled = false,
}: Props) {
  const [divisionForm, setDivisionForm] = useState<TournamentDivisionDraft>(emptyDivisionDraft);
  const [editingDivisionId, setEditingDivisionId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<TournamentDivisionDraft>(emptyDivisionDraft);
  const [savingDivision, setSavingDivision] = useState(false);
  const [savingEditId, setSavingEditId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  // La respuesta de crear o editar una categoría dice si ESTA categoría
  // prendió los datos del tutor: el aviso sale sin recargar.
  const [guardianTurnedOnHere, setGuardianTurnedOnHere] = useState(false);
  const tournamentYear = yearFromTournamentDate(tournamentStartDate);
  // El aviso solo dice la verdad mientras quede alguna categoría de menores;
  // si se renombró, se desactivó o se borró, se explica que siguen prendidos.
  const guardianWasTurnedOn = guardianFieldsAutoEnabled || guardianTurnedOnHere;
  const stillHasMinors = divisions.some((division) => isKidsDivision(division, tournamentYear));
  const showGuardianNotice = guardianWasTurnedOn && stillHasMinors;
  const showGuardianStillOnHint = guardianWasTurnedOn && !stillHasMinors;

  function updateForm<K extends keyof TournamentDivisionDraft>(key: K, value: TournamentDivisionDraft[K]) {
    setDivisionForm((current) => ({ ...current, [key]: value }));
  }

  function updateEditForm<K extends keyof TournamentDivisionDraft>(key: K, value: TournamentDivisionDraft[K]) {
    setEditForm((current) => ({ ...current, [key]: value }));
  }

  async function handleCreateDivision() {
    const name = divisionForm.name.trim();
    if (!name) {
      setError("Escribe el nombre de la categoría.");
      return;
    }
    if (divisionForm.age_min && divisionForm.age_max && Number(divisionForm.age_min) > Number(divisionForm.age_max)) {
      setError("El año de nacimiento mínimo no puede ser mayor que el año de nacimiento máximo.");
      return;
    }
    setSavingDivision(true);
    setError(null);
    try {
      const response = await api.tournaments.createDivision(
        token,
        tournamentId,
        divisionInputFromDraft(divisionForm, divisions.length),
      );
      onDivisionsChange(sortDivisions([...divisions, response.data.division]));
      if (response.data.guardian_fields_auto_enabled) setGuardianTurnedOnHere(true);
      setDivisionForm(emptyDivisionDraft);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No pudimos crear la categoría.");
    } finally {
      setSavingDivision(false);
    }
  }

  async function updateDivision(division: TournamentDivision, payload: Partial<TournamentDivision>) {
    try {
      const response = await api.tournaments.updateDivision(token, tournamentId, division.id, payload);
      onDivisionsChange(sortDivisions(divisions.map((item) => (item.id === division.id ? response.data.division : item))));
      if (response.data.guardian_fields_auto_enabled) setGuardianTurnedOnHere(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No pudimos actualizar la categoría.");
    }
  }

  function draftFromDivision(division: TournamentDivision): TournamentDivisionDraft {
    return {
      name: division.name ?? "",
      gender: division.gender ?? "",
      play_mode: division.play_mode === "doubles" ? "doubles" : "singles",
      team_play: divisionIsTeamPlay(division),
      age_min: division.age_min !== null && division.age_min !== undefined ? String(division.age_min) : "",
      age_max: division.age_max !== null && division.age_max !== undefined ? String(division.age_max) : "",
      level_label: division.level_label ?? "",
      registration_cost: division.registration_cost ?? "",
      max_participants: division.max_participants !== null && division.max_participants !== undefined ? String(division.max_participants) : "",
      registration_deadline: division.registration_deadline ?? "",
    };
  }

  // Rango permitido del cierre propio: del cierre general a la fecha final
  // del torneo (puede caer después del inicio: p. ej. dobles que se juegan
  // después del singles). Sin fecha final, sin tope.
  const deadlineMin = tournamentRegistrationDeadline || undefined;
  const deadlineMax = tournamentEndDate || undefined;
  const deadlineHint =
    "Vacío = usa la fecha general. No puede ser antes del cierre general ni después de la fecha final del torneo. Si cae después del inicio, esta categoría sigue recibiendo inscripciones hasta ese día. Para quitarlo, borra la fecha.";
  const ownDeadlineCount = divisionsWithOwnDeadline(divisions);

  function deadlineField(value: string, onChange: (value: string) => void) {
    return (
      <label className="division-deadline-field">
        <span className="hint-label">
          Cierre de inscripciones (opcional)
          <FieldHint>{deadlineHint}</FieldHint>
        </span>
        <input
          type="date"
          value={value}
          min={deadlineMin}
          max={deadlineMax}
          onChange={(e) => onChange(e.target.value)}
          aria-label="Cierre de inscripciones de la categoría"
        />
        <span className="muted">
          {value ? `Esta categoría cierra el ${shortDateEs(value)}.` : "Igual que el torneo."}
        </span>
      </label>
    );
  }

  function beginEditDivision(division: TournamentDivision) {
    setEditingDivisionId(division.id);
    setEditForm(draftFromDivision(division));
    setError(null);
  }

  function cancelEditDivision() {
    setEditingDivisionId(null);
    setEditForm(emptyDivisionDraft);
    setError(null);
  }

  async function handleSaveDivisionEdit(division: TournamentDivision) {
    const name = editForm.name.trim();
    if (!name) {
      setError("Escribe el nombre de la categoría.");
      return;
    }
    if (editForm.age_min && editForm.age_max && Number(editForm.age_min) > Number(editForm.age_max)) {
      setError("El año de nacimiento mínimo no puede ser mayor que el año de nacimiento máximo.");
      return;
    }
    setSavingEditId(division.id);
    setError(null);
    try {
      const payload = {
        ...divisionInputFromDraft(editForm, division.sort_order),
        is_active: division.is_active,
      };
      const response = await api.tournaments.updateDivision(token, tournamentId, division.id, payload);
      onDivisionsChange(sortDivisions(divisions.map((item) => (item.id === division.id ? response.data.division : item))));
      if (response.data.guardian_fields_auto_enabled) setGuardianTurnedOnHere(true);
      setEditingDivisionId(null);
      setEditForm(emptyDivisionDraft);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No pudimos actualizar la categoría.");
    } finally {
      setSavingEditId(null);
    }
  }

  // «Guardar y continuar» no ve este estado: se le avisa qué quedó a medias
  // (torneo 246: la categoría escrita sin «Agregar» se perdía sin aviso).
  const editingDivision = editingDivisionId != null ? divisions.find((item) => item.id === editingDivisionId) ?? null : null;
  const pendingNewName = divisionForm.name.trim();
  const editIsDirty = editingDivision
    ? JSON.stringify(editForm) !== JSON.stringify(draftFromDivision(editingDivision))
    : false;
  const pendingMessage = pendingNewName
    ? `Tienes la categoría «${pendingNewName}» escrita sin agregar: dale «Agregar categoría» o borra el nombre.`
    : editIsDirty && editingDivision
      ? `Tienes cambios sin guardar en la categoría «${editingDivision.name}»: dale «Guardar» o «Cancelar».`
      : null;
  useEffect(() => {
    onPendingChange?.(pendingMessage);
  }, [onPendingChange, pendingMessage]);

  // Este gestor vive DENTRO del formulario del Paso 1: Enter en un campo de
  // la categoría enviaba todo «Guardar y continuar» y la categoría tecleada
  // nunca se agregaba (reporte Eduardo 2026-10-01). Enter agrega/guarda la
  // categoría y no sale de la pantalla.
  function enterRuns(action: () => void) {
    return (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== "Enter") return;
      if ((event.target as HTMLElement).tagName !== "INPUT") return;
      event.preventDefault();
      action();
    };
  }

  async function toggleDivision(division: TournamentDivision) {
    await updateDivision(division, { is_active: !division.is_active });
  }

  return (
    <section id="categorias" className={`card tournament-divisions-card${compact ? " tournament-divisions-card--compact" : ""}`}>
      <div className="setup-call-card__heading">
        <span>04</span>
        <div>
          <h2>Categorías del torneo</h2>
          <p>Subtorneos competitivos: aquí se listan las categorías que el admin agrega para convocatoria e inscripción.</p>
        </div>
      </div>
      {error ? <p className="form-error">{error}</p> : null}
      <div className="division-deadline-summary">
        <p className="muted">
          {tournamentRegistrationDeadline ? (
            <>Cierre general del torneo: <strong>{weekdayDateEs(tournamentRegistrationDeadline)}</strong>. </>
          ) : (
            <>El torneo no tiene cierre general de inscripciones. </>
          )}
          Si una categoría necesita más días, ponle su propio cierre.
        </p>
        {divisions.length > 0 ? (
          <div className="division-deadline-summary__chips">
            <span className="division-summary-chip">
              <b>✓</b> {divisions.length} {divisions.length === 1 ? "categoría" : "categorías"}
            </span>
            {ownDeadlineCount > 0 ? (
              <span className="division-summary-chip">
                {ownDeadlineCount} {ownDeadlineCount === 1 ? "categoría con cierre propio" : "categorías con cierre propio"}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
      {showGuardianNotice ? (
        <p className="guardian-minors-notice" role="status" data-no-toast>
          {GUARDIAN_AUTO_ENABLED_NOTICE}
        </p>
      ) : null}
      {showGuardianStillOnHint ? (
        <p className="guardian-minors-hint" role="status" data-no-toast>
          {GUARDIAN_STILL_ON_HINT}
        </p>
      ) : null}
      <div
        className="setup-call-grid setup-call-grid--two tournament-division-form"
        onKeyDown={enterRuns(() => { if (!savingDivision) void handleCreateDivision(); })}
      >
        <label>
          Nombre
          <input value={divisionForm.name} onChange={(e) => updateForm("name", e.target.value)} placeholder="12 y menores Varonil" />
        </label>
        <label>
          Género (opcional)
          <select className="t123-select" value={divisionForm.gender} onChange={(e) => updateForm("gender", e.target.value)}>
            <option value="">Abierta / sin restricción</option>
            <option value="male">Varonil</option>
            <option value="female">Femenil</option>
            <option value="mixed">Mixto</option>
          </select>
        </label>
        <label>
          Modalidad de esta categoría
          <select
            className="t123-select"
            value={divisionModality(divisionForm)}
            onChange={(e) => setDivisionForm((current) => applyDivisionModality(current, e.target.value as DivisionModality))}
          >
            {DIVISION_MODALITY_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
          {divisionForm.team_play ? (
            <span className="muted">Se inscriben parejas; cada enfrentamiento se juega en líneas. Las líneas y la alineación se afinan en el Paso 5.</span>
          ) : null}
        </label>
        <label>
          <span className="hint-label">
            Nacidos desde (año)
            <FieldHint>Año de nacimiento más antiguo que acepta la categoría: los mayores del grupo. Ej. "10 y menores" en 2026 → 2016. Se evalúa por año calendario del torneo, no por cumpleaños.</FieldHint>
          </span>
          <input type="number" min="1900" max="2100" placeholder="Ej. 2016" value={divisionForm.age_min} onChange={(e) => updateForm("age_min", e.target.value)} />
        </label>
        <label>
          <span className="hint-label">
            Nacidos hasta (año)
            <FieldHint>Año de nacimiento más reciente que acepta la categoría: los más chicos del grupo. Ej. "de 8 a 10 años" en 2026 → 2018. En una "X y menores" puedes dejarlo vacío (acepta a los más chicos). Ambos vacíos = sin límite de edad.</FieldHint>
          </span>
          <input type="number" min="1900" max="2100" placeholder="Ej. 2018" value={divisionForm.age_max} onChange={(e) => updateForm("age_max", e.target.value)} />
        </label>
        <BirthYearFeedback
          name={divisionForm.name}
          ageMin={divisionForm.age_min}
          ageMax={divisionForm.age_max}
          tournamentYear={tournamentYear}
        />
        <label>
          Nivel (opcional)
          <input value={divisionForm.level_label} onChange={(e) => updateForm("level_label", e.target.value)} placeholder="A, B, Intermedios…" />
        </label>
        <label>
          <span className="hint-label">
            Costo específico (opcional)
            <FieldHint>Si lo dejas vacío, esta categoría usa el costo general del torneo.</FieldHint>
          </span>
          <input value={divisionForm.registration_cost} onChange={(e) => updateForm("registration_cost", e.target.value)} placeholder="Ej. $700" />
        </label>
        <label>
          <span className="hint-label">
            Cupo máximo (opcional)
            <FieldHint>Al llegar a este número de inscripciones aprobadas, el link público cerrará esta categoría (Cupo lleno).</FieldHint>
          </span>
          <input type="number" min="1" placeholder="Ej. 16" value={divisionForm.max_participants} onChange={(e) => updateForm("max_participants", e.target.value)} />
        </label>
        {deadlineField(divisionForm.registration_deadline ?? "", (value) => updateForm("registration_deadline", value))}
        <div className="form-actions tournament-division-form__actions">
          <button className="button button--primary" type="button" onClick={() => { void handleCreateDivision(); }} disabled={savingDivision}>
            {savingDivision ? "Guardando…" : "Agregar categoría"}
          </button>
        </div>
      </div>
      <div className="tournament-division-list">
        {divisions.length === 0 ? (
          <p className="muted">Aún no hay categorías. Si no creas ninguna, el registro público seguirá funcionando como torneo único.</p>
        ) : null}
        {divisions.map((division) => {
          const isEditing = editingDivisionId === division.id;
          return (
          <article className={`tournament-division-item${isEditing ? " tournament-division-item--editing" : ""}`} key={division.id}>
            {isEditing ? (
              <div
                className="setup-call-grid setup-call-grid--two tournament-division-edit-form"
                onKeyDown={enterRuns(() => { if (savingEditId !== division.id) void handleSaveDivisionEdit(division); })}
              >
                <label>
                  Nombre
                  <input value={editForm.name} onChange={(e) => updateEditForm("name", e.target.value)} />
                </label>
                <label>
                  Género (opcional)
                  <select className="t123-select" value={editForm.gender} onChange={(e) => updateEditForm("gender", e.target.value)}>
                    <option value="">Abierta / sin restricción</option>
                    <option value="male">Varonil</option>
                    <option value="female">Femenil</option>
                    <option value="mixed">Mixto</option>
                  </select>
                </label>
                <label>
                  Modalidad
                  <select
                    className="t123-select"
                    value={divisionModality(editForm)}
                    onChange={(e) => setEditForm((current) => applyDivisionModality(current, e.target.value as DivisionModality))}
                  >
                    {DIVISION_MODALITY_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </label>
                <label>
                  <span className="hint-label">
                    Nacidos desde (año)
                    <FieldHint>Año de nacimiento más antiguo que acepta la categoría: los mayores del grupo. Ej. "10 y menores" en 2026 → 2016. Se evalúa por año calendario del torneo, no por cumpleaños.</FieldHint>
                  </span>
                  <input type="number" min="1900" max="2100" placeholder="Ej. 2016" value={editForm.age_min} onChange={(e) => updateEditForm("age_min", e.target.value)} />
                </label>
                <label>
                  <span className="hint-label">
                    Nacidos hasta (año)
                    <FieldHint>Año de nacimiento más reciente que acepta la categoría: los más chicos del grupo. Ej. "de 8 a 10 años" en 2026 → 2018. En una "X y menores" puedes dejarlo vacío (acepta a los más chicos). Ambos vacíos = sin límite de edad.</FieldHint>
                  </span>
                  <input type="number" min="1900" max="2100" placeholder="Ej. 2018" value={editForm.age_max} onChange={(e) => updateEditForm("age_max", e.target.value)} />
                </label>
                <BirthYearFeedback
                  name={editForm.name}
                  ageMin={editForm.age_min}
                  ageMax={editForm.age_max}
                  tournamentYear={tournamentYear}
                />
                <label>
                  Nivel (opcional)
                  <input value={editForm.level_label} onChange={(e) => updateEditForm("level_label", e.target.value)} />
                </label>
                <label>
                  <span className="hint-label">
                    Costo específico (opcional)
                    <FieldHint>Vacío = costo general del torneo.</FieldHint>
                  </span>
                  <input value={editForm.registration_cost} onChange={(e) => updateEditForm("registration_cost", e.target.value)} placeholder="Ej. $700" />
                </label>
                <label>
                  <span className="hint-label">
                    Cupo máximo (opcional)
                    <FieldHint>Al llegar a este número de inscripciones aprobadas, el link público cerrará esta categoría (Cupo lleno).</FieldHint>
                  </span>
                  <input type="number" min="1" placeholder="Ej. 16" value={editForm.max_participants} onChange={(e) => updateEditForm("max_participants", e.target.value)} />
                </label>
                {deadlineField(editForm.registration_deadline ?? "", (value) => updateEditForm("registration_deadline", value))}
              </div>
            ) : (
              <div>
                <strong>{division.name}</strong>
                <p className="muted">{divisionDetailsLabel(division)}</p>
                {divisionBirthYearLabel(division, tournamentYear) ? (
                  <small className="muted">
                    Permitidos: {divisionBirthYearLabel(division, tournamentYear)} · Se evalúa por año calendario {tournamentYear}.
                  </small>
                ) : null}
                {division.max_participants ? (
                  <small className="muted">
                    Cupo máximo: {division.max_participants} inscripciones aprobadas · al llenarse, el link público cierra esta categoría.
                  </small>
                ) : null}
                <small className="muted division-deadline-line">
                  Cierre de inscripciones:{" "}
                  {division.registration_deadline ? (
                    <b className="division-deadline-line__date">{shortDateEs(division.registration_deadline)}</b>
                  ) : (
                    "igual que el torneo"
                  )}
                </small>
              </div>
            )}
            <div className="division-actions">
              {isEditing ? (
                <>
                  <button className="button button--primary" type="button" onClick={() => { void handleSaveDivisionEdit(division); }} disabled={savingEditId === division.id}>
                    {savingEditId === division.id ? "Guardando…" : "Guardar"}
                  </button>
                  <button className="button button--ghost" type="button" onClick={cancelEditDivision} disabled={savingEditId === division.id}>
                    Cancelar
                  </button>
                </>
              ) : (
                <>
                  {showFormatActions && onCopyTournamentFormat ? (
                    <button className="button button--ghost" type="button" onClick={() => onCopyTournamentFormat(division)}>
                      Usar formato general
                    </button>
                  ) : null}
                  <button className="button button--ghost" type="button" onClick={() => beginEditDivision(division)}>
                    Editar
                  </button>
                  <button className="button button--ghost" type="button" onClick={() => { void toggleDivision(division); }}>
                    {division.is_active ? "Desactivar" : "Activar"}
                  </button>
                </>
              )}
            </div>
          </article>
        );
        })}
      </div>
    </section>
  );
}
